import { OnboardingStatusId } from '@/store/advisorInvestorsApi';

export type BucketKind = 'onboarding' | 'frequency' | 'recency';

/**
 * Login frequency buckets, matching AdvisorLoginFrequencyProcessor.FrequencyRange.
 */
export enum LoginFrequencyId {
  EveryWeek = 1,
  Every2Weeks = 2,
  EveryMonth = 3,
  Every3Months = 4,
  MoreThan3Months = 5,
  NoLoginsInRange = 6,
}

/**
 * Last-login recency buckets, matching InvestorLoginRecencyProcessor.RecencyRange.
 */
export enum LoginRecencyId {
  Within7Days = 1,
  Days8To30 = 2,
  Days31To60 = 3,
  Days61To90 = 4,
  Days91To180 = 5,
  OverSixMonths = 6,
  NeverLoggedIn = 7,
}

const BUCKET_KEYS: Record<BucketKind, Record<number, string>> = {
  onboarding: {
    [OnboardingStatusId.NotInvited]: 'clientActivity.buckets.notInvited',
    [OnboardingStatusId.Invited]: 'clientActivity.buckets.invited',
    [OnboardingStatusId.Active]: 'clientActivity.buckets.active',
    [OnboardingStatusId.OnlyOrion]: 'clientActivity.buckets.onlyOrion',
  },
  frequency: {
    [LoginFrequencyId.EveryWeek]: 'clientActivity.buckets.everyWeek',
    [LoginFrequencyId.Every2Weeks]: 'clientActivity.buckets.every2Weeks',
    [LoginFrequencyId.EveryMonth]: 'clientActivity.buckets.everyMonth',
    [LoginFrequencyId.Every3Months]: 'clientActivity.buckets.every3Months',
    [LoginFrequencyId.MoreThan3Months]:
      'clientActivity.buckets.moreThan3Months',
    [LoginFrequencyId.NoLoginsInRange]:
      'clientActivity.buckets.noLoginsInRange',
  },
  recency: {
    [LoginRecencyId.Within7Days]: 'clientActivity.buckets.within7Days',
    [LoginRecencyId.Days8To30]: 'clientActivity.buckets.days8To30',
    [LoginRecencyId.Days31To60]: 'clientActivity.buckets.days31To60',
    [LoginRecencyId.Days61To90]: 'clientActivity.buckets.days61To90',
    [LoginRecencyId.Days91To180]: 'clientActivity.buckets.days91To180',
    [LoginRecencyId.OverSixMonths]: 'clientActivity.buckets.overSixMonths',
    [LoginRecencyId.NeverLoggedIn]: 'clientActivity.buckets.neverLoggedIn',
  },
};

/**
 * The translation key for a bucket, or undefined when the API sent an id this
 * build does not know about.
 */
export function bucketLabelKey(
  kind: BucketKind,
  id: number,
): string | undefined {
  return BUCKET_KEYS[kind][id];
}

/**
 * Display name for a bucket.
 *
 * The API labels these buckets in English, so they are re-resolved here by their
 * stable numeric id. An id with no mapping falls back to whatever the API sent,
 * which keeps a bucket added on the server readable rather than blank.
 */
export function bucketLabel(
  translate: (key: string, fallback: string) => string,
  kind: BucketKind,
  id: number,
  apiName: string,
): string {
  const key = bucketLabelKey(kind, id);
  return key ? translate(key, apiName) : apiName;
}
